import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrashAlt, faEdit, faEye } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import AddTeacher from "../AddTeacher/AddTeacher";
import ViewTeacher from "../ViewTeacher/ViewTeacher";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Teacher.css";

const API_URL = "https://aadyaacademy.org/api";

function Teacher() {
  const [teacherList, setTeacherList] = useState([]);
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [departments, setDepartments] = useState([]);
  const [addingTeacher, setAddingTeacher] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [viewingTeacher, setViewingTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);

  const fetchData = async () => {
    try {
      const teacherResponse = await axios.get(`${API_URL}/teachers`);
      let sortedTeachers = (teacherResponse.data || []).slice().sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      // Process teacher data to handle comma-separated strings
      sortedTeachers = sortedTeachers.map((teacher) => ({
        ...teacher,
        classteacher: teacher.classteacher ? teacher.classteacher.split(",") : [],
        subject: teacher.subject ? teacher.subject.split(",") : [],
        section: teacher.section ? teacher.section.split(",") : [],
      }));
      console.log("Processed teacher data:", sortedTeachers);
      setTeacherList(sortedTeachers);

      const departmentResponse = await axios.get(`${API_URL}/departments`);
      setDepartments(departmentResponse.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = [...teacherList];

    if (searchTerm) {
      filtered = filtered.filter((teacher) => {
        const classTeacherClasses = teacher.class_teacher_for?.map(ct => ct.class) || teacher.classteacher || [];
        const classTeacherSections = teacher.class_teacher_for?.map(ct => ct.section) || teacher.section || [];
        const subjectTeacherClasses = teacher.subject_teacher_for?.map(st => st.class) || teacher.classteacher || [];
        const subjectTeacherSections = teacher.subject_teacher_for?.map(st => st.section) || teacher.section || [];
        const subjects = teacher.subject_teacher_for?.map(st => st.subject) || teacher.subject || [];
        return [
          teacher.name,
          teacher.department,
          ...classTeacherClasses,
          ...classTeacherSections,
          ...subjectTeacherClasses,
          ...subjectTeacherSections,
          ...subjects,
        ].some((field) =>
          field?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }

    if (departmentFilter) {
      filtered = filtered.filter((teacher) => teacher.department === departmentFilter);
    }

    if (classFilter) {
      filtered = filtered.filter((teacher) => {
        const classTeacherClasses = teacher.class_teacher_for?.map(ct => ct.class) || teacher.classteacher || [];
        const subjectTeacherClasses = teacher.subject_teacher_for?.map(st => st.class) || teacher.classteacher || [];
        return [...classTeacherClasses, ...subjectTeacherClasses].includes(classFilter);
      });
    }

    if (sectionFilter) {
      filtered = filtered.filter((teacher) => {
        const classTeacherSections = teacher.class_teacher_for?.map(ct => ct.section) || teacher.section || [];
        const subjectTeacherSections = teacher.subject_teacher_for?.map(st => st.section) || teacher.section || [];
        return [...classTeacherSections, ...subjectTeacherSections].includes(sectionFilter);
      });
    }

    filtered.sort((a, b) => a.name.localeCompare(b.name));
    setFilteredTeachers(filtered);
  }, [searchTerm, departmentFilter, classFilter, sectionFilter, teacherList]);

  const uniqueClasses = Array.from(
    new Set(
      teacherList
        .flatMap(t => [
          ...(t.class_teacher_for?.map(ct => ct.class) || t.classteacher || []),
          ...(t.subject_teacher_for?.map(st => st.class) || t.classteacher || []),
        ])
        .filter(Boolean)
    )
  ).sort();

  const uniqueSections = Array.from(
    new Set(
      teacherList
        .flatMap(t => [
          ...(t.class_teacher_for?.map(ct => ct.section) || t.section || []),
          ...(t.subject_teacher_for?.map(st => st.section) || t.section || []),
        ])
        .filter(Boolean)
    )
  ).sort();

  // Function to format class teacher field
  const formatClassTeacher = (classTeacherFor, classteacher) => {
    if (classTeacherFor && Array.isArray(classTeacherFor) && classTeacherFor.length > 0) {
      return classTeacherFor
        .map(ct => (ct.class && ct.section ? `${ct.class}-${ct.section}` : ct.class || ''))
        .filter(Boolean)
        .join(', ');
    }
    return classteacher && Array.isArray(classteacher) && classteacher.length > 0
      ? classteacher.join(', ')
      : '';
  };

  // Function to format subject teacher field
  const formatSubjectTeacher = (subjectTeacherFor, classteacher, section) => {
    if (subjectTeacherFor && Array.isArray(subjectTeacherFor) && subjectTeacherFor.length > 0) {
      return Array.from(new Set(
        subjectTeacherFor
          .map(st => (st.class && st.section ? `${st.class}-${st.section}` : ''))
          .filter(Boolean)
      )).join(', ');
    }
    if (classteacher && section && classteacher.length > 0 && section.length > 0) {
      return classteacher.map(cls => `${cls}-${section[0] || ''}`).filter(Boolean).join(', ');
    }
    return '';
  };

  // Function to format subjects
  const formatSubjects = (subjectTeacherFor, subject) => {
    if (subjectTeacherFor && Array.isArray(subjectTeacherFor) && subjectTeacherFor.length > 0) {
      return Array.from(new Set(subjectTeacherFor.map(st => st.subject).filter(Boolean))).join(', ');
    }
    return subject && Array.isArray(subject) && subject.length > 0
      ? subject.join(', ')
      : '';
  };

  const handleCancel = () => {
    setAddingTeacher(false);
    setEditingTeacher(null);
  };

  const confirmDelete = (id) => {
    setSelectedTeacherId(id);
    setShowDeletePopup(true);
  };

  const cancelDelete = () => {
    setShowDeletePopup(false);
    setSelectedTeacherId(null);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/teachers/${selectedTeacherId}`);
      fetchData();
      toast.success("Teacher deleted successfully!");
    } catch (error) {
      console.error("Error deleting teacher:", error);
      toast.error("Failed to delete teacher. Please try again.");
    } finally {
      setShowDeletePopup(false);
      setSelectedTeacherId(null);
    }
  };

  const handleEdit = (teacher) => {
    setEditingTeacher(teacher);
    setAddingTeacher(true);
  };

  const handleCancelView = () => {
    setViewingTeacher(null);
  };

  const handleRowClick = (teacher, e) => {
    if (e.target.closest(".actions button")) {
      return;
    }
    setViewingTeacher(teacher);
  };

  if (addingTeacher) {
    return (
      <>
        <AddTeacher handleCancel={handleCancel} fetchData={fetchData} teacher={editingTeacher} />
        <ToastContainer />
      </>
    );
  }

  if (viewingTeacher) {
    return (
      <>
        <ViewTeacher teacher={viewingTeacher} onClose={handleCancelView} />
        <ToastContainer />
      </>
    );
  }

  return (
    <div className="Teacher">
      <ToastContainer />
      <div className="Teacher-table">
        <div className="table-header">
          <div className="table-header-top">
            <div className="header-container" style={{ display: "flex", alignItems: "center" }}>
              <h2>Teacher List: {filteredTeachers.length}</h2>
            </div>
          </div>
          <div className="table-header-btm">
            <input
              type="text"
              className="search-input"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="department-filter"
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <option value="">Department</option>
              {departments.map((department) => (
                <option key={department.id} value={department.names}>
                  {department.names}
                </option>
              ))}
            </select>
            <select
              className="class-filter"
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
            >
              <option value="">Class</option>
              {uniqueClasses.map((classItem) => (
                <option key={classItem} value={classItem}>
                  {classItem}
                </option>
              ))}
            </select>
            <select
              className="section-filter"
              value={sectionFilter}
              onChange={(e) => setSectionFilter(e.target.value)}
            >
              <option value="">Section</option>
              {uniqueSections.map((section) => (
                <option key={section} value={section}>
                  {section}
                </option>
              ))}
            </select>
            <button onClick={() => setAddingTeacher(true)} className="add-teacher-btn">
              <FontAwesomeIcon icon={faPlus} /> Add New
            </button>
          </div>
        </div>
        <div className="table-content">
          <table>
            <thead>
              <tr>
                <th className="thead-content">S.No</th>
                <th className="thead-content">Name</th>
                <th className="thead-content">Department</th>
                <th className="thead-content">Class Teacher</th>
                <th className="thead-content">Subject Teacher</th>
                <th className="thead-content">Subject</th>
                <th className="thead-content">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>Loading...</td>
                </tr>
              ) : filteredTeachers.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>No Teachers found</td>
                </tr>
              ) : (
                filteredTeachers.map((teacher, index) => (
                  <tr
                    key={teacher.id}
                    onClick={(e) => handleRowClick(teacher, e)}
                    style={{ cursor: "pointer" }}
                    className="teacher-row"
                  >
                    <td className="tbody-content">{index + 1}</td>
                    <td className="tbody-content">{teacher.name}</td>
                    <td className="tbody-content">{teacher.department}</td>
                    <td className="tbody-content">{formatClassTeacher(teacher.class_teacher_for, teacher.classteacher)}</td>
                    <td className="tbody-content">{formatSubjectTeacher(teacher.subject_teacher_for, teacher.classteacher, teacher.section)}</td>
                    <td className="tbody-content">{formatSubjects(teacher.subject_teacher_for, teacher.subject)}</td>
                    <td className="actions">
                      <button className="view-btn" onClick={() => setViewingTeacher(teacher)}>
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button className="edit-btn" onClick={() => handleEdit(teacher)}>
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button className="delete-btn" onClick={() => confirmDelete(teacher.id)}>
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showDeletePopup && (
        <div className="modal delete-confirmation">
          <div className="modal-content">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this teacher?</p>
            <div className="modal-actions">
              <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
              <button className="btn btn-secondary" onClick={cancelDelete}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Teacher;